/**
 * The setup the workspace was last *verified* to hold: a profile that was saved,
 * read back and matched against the setup it was meant to carry.
 *
 * Discard reverts to this record, never to whatever the profile dropdown happens
 * to show — a selection is an intent, while this is evidence. The record is
 * immutable and self-contained (profile, fingerprint, revision, document), so a
 * later restore can prove it still belongs to the document in front of the user
 * instead of reloading a profile from a different model.
 */

import { areSetupDocumentIdentitiesEqual, type SetupDocumentIdentity } from './setupFingerprint';

export interface VerifiedSetupBaseline {
  /** The profile key the verified setup was read back from. */
  readonly profileKey: string;
  /** Canonical fingerprint of the setup that read-back confirmed. */
  readonly setupFingerprint: string;
  /** The revision the verification belongs to. */
  readonly revisionId: number;
  /** The document the verification was performed against. */
  readonly identity: SetupDocumentIdentity;
}

export interface VerifiedSetupBaselineInput {
  profileKey: string;
  setupFingerprint: string | null;
  revisionId: number;
  identity: SetupDocumentIdentity | null;
}

export type VerifiedBaselineRefusalReason = 'no-baseline' | 'document-changed';

export type VerifiedBaselineRestore =
  | { kind: 'restore'; profileKey: string; baseline: VerifiedSetupBaseline }
  | { kind: 'refused'; reason: VerifiedBaselineRefusalReason };

const noBaseline: VerifiedBaselineRestore = { kind: 'refused', reason: 'no-baseline' };
const documentChanged: VerifiedBaselineRestore = { kind: 'refused', reason: 'document-changed' };

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const isRecordedRevisionId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

/** A document is known only when every part of it is. */
const readIdentity = (value: unknown): SetupDocumentIdentity | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const candidate = value as Partial<SetupDocumentIdentity>;
  const projectKey = trimmed(candidate.projectKey);
  const documentTitle = trimmed(candidate.documentTitle);
  const documentPath = trimmed(candidate.documentPath);
  if (!projectKey || !documentTitle || !documentPath) {
    return null;
  }

  return Object.freeze({ projectKey, documentTitle, documentPath });
};

/**
 * Records a verification, or nothing at all when any part of it is missing: a
 * half-known baseline would send Discard to the wrong profile or the wrong
 * document, which is worse than having no baseline to offer.
 */
export const createVerifiedSetupBaseline = (
  input: VerifiedSetupBaselineInput
): VerifiedSetupBaseline | null => {
  const profileKey = trimmed(input.profileKey);
  const setupFingerprint = trimmed(input.setupFingerprint);
  const identity = readIdentity(input.identity);
  if (!profileKey || !setupFingerprint || !identity || !isRecordedRevisionId(input.revisionId)) {
    return null;
  }

  return Object.freeze({ profileKey, setupFingerprint, revisionId: input.revisionId, identity });
};

/** The profile Discard may reload, or why it may not reload anything. */
export const resolveVerifiedBaselineRestore = (
  baseline: VerifiedSetupBaseline | null,
  context: { identity: SetupDocumentIdentity | null }
): VerifiedBaselineRestore => {
  if (!baseline) {
    return noBaseline;
  }
  // An unidentified document is not proof of the same document.
  if (!context.identity || !areSetupDocumentIdentitiesEqual(baseline.identity, context.identity)) {
    return documentChanged;
  }

  return { kind: 'restore', profileKey: baseline.profileKey, baseline };
};

/**
 * Reads a persisted record. Anything that is not a complete record is dropped
 * rather than patched up — a repaired baseline is a claim nobody verified.
 */
export const sanitizeVerifiedSetupBaseline = (value: unknown): VerifiedSetupBaseline | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const candidate = value as Partial<VerifiedSetupBaseline>;
  if (!isRecordedRevisionId(candidate.revisionId)) {
    return null;
  }

  return createVerifiedSetupBaseline({
    profileKey: trimmed(candidate.profileKey),
    setupFingerprint: trimmed(candidate.setupFingerprint),
    revisionId: candidate.revisionId,
    identity: readIdentity(candidate.identity),
  });
};
